<template>
  <div class="price-trend-container">
    <div class="chart-wrapper" ref="chart"></div>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { debounce } from 'lodash';
import fecha from 'element-ui/src/utils/date';

export default {
  props: {
    priceData: {
      type: Array,
      default: () => [],
      required: true,
    },
    dateData: {
      type: Array,
      default: () => [],
      required: true,
    },
    color: {
      type: String,
      default: '#409EFF',
    },
  },
  data() {
    return {
      chart: null,
      resizeObserver: null,
    };
  },
  watch: {
    priceData: {
      handler() {
        this.updateChart();
      },
      deep: true,
    },
    dateData: {
      handler() {
        this.updateChart();
      },
      deep: true,
    },
    color() {
      this.updateChart();
    },
  },
  mounted() {
    this.initChart();
    window.addEventListener('resize', this.handleResize);
    this.observeResize();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.chart) {
      this.chart.dispose();
    }
  },
  methods: {
    initChart() {
      this.chart = echarts.init(this.$refs.chart);
      this.updateChart();
    },
    updateChart() {
      if (!this.chart || !this.priceData.length || !this.dateData.length)
        return;

      // 计算合适的y轴间隔
      const maxValue = Math.max(...this.priceData);
      const minValue = Math.min(...this.priceData);
      const range = maxValue - minValue;
      
      // 动态计算合适的y轴间隔
      let interval = 1;
      if (range > 1000) {
        interval = 200;
      } else if (range > 500) {
        interval = 100;
      } else if (range > 200) {
        interval = 50;
      } else if (range > 100) {
        interval = 20;
      } else if (range > 50) {
        interval = 10;
      } else if (range > 20) {
        interval = 5;
      } else if (range > 10) {
        interval = 2;
      }

      // 计算最小值和最大值，确保间隔一致
      const min = Math.floor(minValue / interval) * interval;
      const max = Math.ceil(maxValue / interval) * interval;

      const option = {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            const date = fecha.format(
              new Date(params[0].axisValue),
              'yyyy-MM-dd',
            );
            const value = params[0].value;
            return `
              <div style="font-weight:bold;margin-bottom:5px;font-size:18px">${date}</div>
              <div style="font-size:18px">价格: <span style="color:${this.color};font-weight:bold;font-size:18px">${value}</span></div>
            `;
          },
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '10%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: this.dateData,
          axisLine: {
            lineStyle: {
              color: '#dcdfe6',
            },
          },
          axisTick: {
            alignWithLabel: true,
          },
          axisLabel: {
            color: '#909399',
            fontSize: 16,
            formatter: (value) => {
              // 根据数据量自动调整日期显示格式
              if (this.dateData.length > 10) {
                return fecha.format(new Date(value), 'MM-dd');
              }
              return fecha.format(new Date(value), 'yyyy-MM-dd');
            },
          },
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          axisLine: {
            show: true,
            lineStyle: {
              color: '#dcdfe6',
            },
          },
          axisLabel: {
            color: '#909399',
            formatter: '{value}',
            fontSize: 16,
            // 隐藏第一个标签（靠近原点的标签）
            showMinLabel: false,
          },
          splitLine: {
            lineStyle: {
              color: '#ebeef5',
              type: 'dashed',
            },
          },
          // 设置固定间隔
          interval: interval,
          min: min,
          max: max,
        },
        series: [
          {
            name: '价格',
            type: 'line',
            data: this.priceData,
            symbol: 'circle',
            symbolSize: 6,
            itemStyle: {
              color: this.color,
            },
            lineStyle: {
              width: 2,
              color: this.color,
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: `rgba(${this.hexToRgb(this.color)}, 0.3)`,
                },
                {
                  offset: 1,
                  color: `rgba(${this.hexToRgb(this.color)}, 0.1)`,
                },
              ]),
            },
            label: {
              show: true,
              position: 'top',
              color: '#606266',
              fontSize: 16,
              formatter: '{c}',
            },
          },
        ],
      };

      this.chart.setOption(option, true);
    },
    handleResize: debounce(function () {
      if (this.chart) {
        this.chart.resize();
      }
    }, 300),
    observeResize() {
      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.$refs.chart);
      }
    },
    hexToRgb(hex) {
      // 将十六进制颜色转换为rgb格式
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `${r}, ${g}, ${b}`;
    },
  },
};
</script>

<style scoped>
.price-trend-container {
  width: 100%;
  height: 100%;
}

.chart-wrapper {
  width: 100%;
  height: 100%;
  min-height: 300px;
}

/* 响应式调整 */
@media (max-width: 1366px) {
  .chart-wrapper {
    min-height: 280px;
  }
}

@media (max-width: 768px) {
  .chart-wrapper {
    min-height: 250px;
  }
}
</style>